import { useEffect, useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';

/**
 * Owns the editable draft of the menu + all mutation helpers used by the admin UI.
 * Keeps AdminPage.jsx thin by concentrating state logic in one place.
 */
export const useMenuDraft = (menu) => {
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (menu && menu.items) {
      const clone = JSON.parse(JSON.stringify(menu));
      Object.keys(clone.items || {}).forEach((k) => {
        const sec = clone.items[k];
        if (sec && Array.isArray(sec.items)) {
          sec.items = sec.items.map((it, i) => ({
            ...it,
            _id: it._id || `${k}-${i}-${Math.random().toString(36).slice(2, 8)}`,
          }));
        }
      });
      setDraft(clone);
    }
  }, [menu]);

  const updateItem = (selectedSub, idx, field, value) => {
    setDraft((prev) => {
      const d = { ...prev };
      d.items = { ...d.items };
      d.items[selectedSub] = { ...d.items[selectedSub] };
      d.items[selectedSub].items = [...d.items[selectedSub].items];
      const item = { ...d.items[selectedSub].items[idx] };
      if (field === 'name_it') item.name = { ...(item.name || {}), it: value };
      else if (field === 'name_en') item.name = { ...(item.name || {}), en: value };
      else if (field === 'desc_it') item.desc = { ...(item.desc || {}), it: value };
      else if (field === 'desc_en') item.desc = { ...(item.desc || {}), en: value };
      else item[field] = value;
      d.items[selectedSub].items[idx] = item;
      return d;
    });
  };

  const addItem = (selectedSub) => {
    setDraft((prev) => {
      const d = { ...prev };
      d.items = { ...d.items };
      d.items[selectedSub] = { ...d.items[selectedSub] };
      d.items[selectedSub].items = [
        ...(d.items[selectedSub].items || []),
        { _id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, name: { it: 'Nuovo piatto', en: 'New item' }, price: '0,00', desc: { it: '', en: '' }, allergens: '' },
      ];
      return d;
    });
  };

  const deleteItem = (selectedSub, idx) => {
    if (!window.confirm('Eliminare questo piatto?')) return;
    setDraft((prev) => {
      const d = { ...prev };
      d.items = { ...d.items };
      d.items[selectedSub] = { ...d.items[selectedSub] };
      d.items[selectedSub].items = d.items[selectedSub].items.filter((_, i) => i !== idx);
      return d;
    });
  };

  const reorderItems = (selectedSub, oldIndex, newIndex) => {
    setDraft((prev) => {
      const d = { ...prev };
      d.items = { ...d.items };
      d.items[selectedSub] = { ...d.items[selectedSub] };
      d.items[selectedSub].items = arrayMove(d.items[selectedSub].items, oldIndex, newIndex);
      return d;
    });
  };

  const reorderSubs = (catSlug, oldIndex, newIndex) => {
    setDraft((prev) => {
      const d = { ...prev };
      d.subCategories = { ...d.subCategories };
      d.subCategories[catSlug] = arrayMove(d.subCategories[catSlug], oldIndex, newIndex);
      return d;
    });
  };

  const updateMainCategoryImage = (slug, image) => {
    setDraft((prev) => ({
      ...prev,
      mainCategories: prev.mainCategories.map((c) => (c.slug === slug ? { ...c, image } : c)),
    }));
  };

  const updateSubCategoryImage = (catSlug, subSlug, image) => {
    setDraft((prev) => {
      const d = { ...prev };
      d.subCategories = { ...d.subCategories };
      d.subCategories[catSlug] = (d.subCategories[catSlug] || []).map((s) =>
        s.slug === subSlug ? { ...s, image } : s
      );
      return d;
    });
  };

  const updateDailySpecialMeta = (field, value) => {
    setDraft((prev) => {
      const d = { ...prev };
      const special = { ...(d.dailySpecial || { active: false, date: '', items: [] }) };
      if (!Array.isArray(special.items)) special.items = [];
      special[field] = value;
      d.dailySpecial = special;
      return d;
    });
  };

  const updateDailySpecialItem = (idx, field, value) => {
    setDraft((prev) => {
      const d = { ...prev };
      const special = { ...(d.dailySpecial || { active: false, date: '', items: [] }) };
      const items = Array.isArray(special.items) ? [...special.items] : [];
      const item = { ...(items[idx] || {}) };
      if (field === 'name_it') item.name = { ...(item.name || {}), it: value };
      else if (field === 'name_en') item.name = { ...(item.name || {}), en: value };
      else if (field === 'description_it') item.description = { ...(item.description || {}), it: value };
      else if (field === 'description_en') item.description = { ...(item.description || {}), en: value };
      else item[field] = value;
      items[idx] = item;
      special.items = items;
      d.dailySpecial = special;
      return d;
    });
  };

  const addDailySpecialItem = () => {
    setDraft((prev) => {
      const d = { ...prev };
      const special = { ...(d.dailySpecial || { active: false, date: '', items: [] }) };
      const items = Array.isArray(special.items) ? [...special.items] : [];
      items.push({ _id: `ds-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, name: { it: '', en: '' }, description: { it: '', en: '' }, price: '', image: '' });
      special.items = items;
      d.dailySpecial = special;
      return d;
    });
  };

  const removeDailySpecialItem = (idx) => {
    if (!window.confirm('Rimuovere questo piatto del giorno?')) return;
    setDraft((prev) => {
      const d = { ...prev };
      const special = { ...(d.dailySpecial || { active: false, date: '', items: [] }) };
      special.items = (special.items || []).filter((_, i) => i !== idx);
      d.dailySpecial = special;
      return d;
    });
  };

  const setTopLevel = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

  const updateRestaurantInfo = (field, value) => {
    setDraft((prev) => ({
      ...prev,
      restaurantInfo: { ...(prev.restaurantInfo || {}), [field]: value },
    }));
  };

  /**
   * Walk the draft and build a list of {id, text} pairs for every EN field that is
   * empty while the IT counterpart has content. `id` encodes the field location so
   * we can route translations back during `applyBatchTranslations`.
   */
  const collectEmptyEnFields = () => {
    if (!draft) return [];
    const jobs = [];
    Object.entries(draft.items || {}).forEach(([subSlug, sec]) => {
      (sec.items || []).forEach((it, idx) => {
        if ((it.name?.it || '').trim() && !(it.name?.en || '').trim()) {
          jobs.push({ id: `item|${subSlug}|${idx}|name`, text: it.name.it });
        }
        if ((it.desc?.it || '').trim() && !(it.desc?.en || '').trim()) {
          jobs.push({ id: `item|${subSlug}|${idx}|desc`, text: it.desc.it });
        }
      });
    });
    const ds = draft.dailySpecial;
    if (ds && Array.isArray(ds.items)) {
      ds.items.forEach((it, idx) => {
        if ((it.name?.it || '').trim() && !(it.name?.en || '').trim()) {
          jobs.push({ id: `special|${idx}|name`, text: it.name.it });
        }
        if ((it.description?.it || '').trim() && !(it.description?.en || '').trim()) {
          jobs.push({ id: `special|${idx}|desc`, text: it.description.it });
        }
      });
    }
    return jobs;
  };

  const applyBatchTranslations = (results) => {
    setDraft((prev) => {
      const d = JSON.parse(JSON.stringify(prev));
      results.forEach((r) => {
        if (!r.ok || !r.translated) return;
        const parts = r.id.split('|');
        if (parts[0] === 'item') {
          const [, subSlug, idxStr, field] = parts;
          const idx = Number(idxStr);
          const sec = d.items[subSlug];
          const it = sec && sec.items && sec.items[idx];
          if (!it) return;
          if (field === 'name') it.name = { ...(it.name || {}), en: r.translated };
          else if (field === 'desc') it.desc = { ...(it.desc || {}), en: r.translated };
        } else if (parts[0] === 'special') {
          const [, idxStr, field] = parts;
          const idx = Number(idxStr);
          const sp = d.dailySpecial;
          const it = sp && Array.isArray(sp.items) && sp.items[idx];
          if (!it) return;
          if (field === 'name') it.name = { ...(it.name || {}), en: r.translated };
          else if (field === 'desc') it.description = { ...(it.description || {}), en: r.translated };
        }
      });
      return d;
    });
  };

  return {
    draft,
    setDraft,
    updateItem,
    addItem,
    deleteItem,
    reorderItems,
    reorderSubs,
    updateMainCategoryImage,
    updateSubCategoryImage,
    updateDailySpecialMeta,
    updateDailySpecialItem,
    addDailySpecialItem,
    removeDailySpecialItem,
    updateRestaurantInfo,
    setTopLevel,
    collectEmptyEnFields,
    applyBatchTranslations,
  };
};

export default useMenuDraft;
